// ** React
import React, { useState, type ReactNode, useRef } from 'react';
import clsx from 'clsx';

// ** MUI Components
import Menu from '@mui/material/Menu';
import Box from '@mui/material/Box';
import SvgIcon from '@mui/material/SvgIcon';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';

// ** Components
import MenuItems, { type MenuItemType } from './MenuItems';
import mergeSxProps from '../shared/mergeSxProps';
import PowerIconButton from './PowerIconButton';

// ** Hooks
import { useTheme } from '@mui/material/styles';
import type { PopoverOrigin } from '@mui/material/Popover';
import type { IconButtonProps } from '@mui/material/IconButton';
import type { SxProps, Theme } from '@mui/material/styles';

type DropdownMenuProps = {
  items: MenuItemType[];
  icon?: string;
  /** Passed to `PowerIconButton` when `icon` is set. */
  hoverIcon?: string;
  /** Passed to `PowerIconButton` when `icon` is set. */
  color?: IconButtonProps['color'];
  title?: ReactNode;
  className?: string;
  selectedItem?: string | null;
  position?: 'left' | 'right' | 'top' | 'bottom';
  loading?: boolean;
  opacity?: number;
  /** Passed to `PowerIconButton` when `icon` is set. */
  padding?: number;
  /** Passed to `PowerIconButton` when `icon` is set. Defaults to `large`. */
  size?: 'small' | 'medium' | 'large';
  setIsOpen?: (isOpen: boolean) => void;
  children?: ReactNode;
};

export default function DropdownMenu(props: DropdownMenuProps) {
  'use memo';

  const {
    items,
    icon,
    hoverIcon,
    color,
    title,
    className,
    selectedItem,
    loading,
    opacity = 1,
    padding,
    size = 'large',
    setIsOpen,
    children,
  } = props;
  let { position = 'right' } = props;
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [anchorEl, setAnchorEl] = useState<Element | null>(null);

  const { direction } = useTheme();
  if (direction === 'rtl' && (position === 'left' || position === 'right')) {
    position = position === 'right' ? 'left' : 'right';
  }

  const handleDropdownOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (containerRef.current) {
      setAnchorEl(containerRef.current);
      setIsOpen?.(true);
    }
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
    setIsOpen?.(false);
  };

  // position === 'right'
  let anchorOrigin: PopoverOrigin = { vertical: 'center', horizontal: 'right' };
  let transformOrigin: PopoverOrigin = { vertical: 'center', horizontal: 'left' };
  let arrowPosition = { top: 'calc(50% - 10px)', right: '-18px', bottom: 'auto', left: 'auto' };
  let menuMargin: SxProps<Theme> = { ml: '8px' };
  let points = '10 0 0 10 10 20';

  if (position === 'left') {
    anchorOrigin = { vertical: 'center', horizontal: 'left' };
    transformOrigin = { vertical: 'center', horizontal: 'right' };
    arrowPosition = { top: 'calc(50% - 10px)', right: 'auto', bottom: 'auto', left: '-18px' };
    menuMargin = { ml: '-8px' };
    points = '10 0 20 10 10 20';
  } else if (position === 'top') {
    anchorOrigin = { vertical: 'top', horizontal: 'center' };
    transformOrigin = { vertical: 'bottom', horizontal: 'center' };
    arrowPosition = { top: '-18px', right: 'calc(50% - 10px)', bottom: 'auto', left: 'auto' };
    menuMargin = { mt: '-8px' };
    points = '0 10 10 20 20 10';
  } else if (position === 'bottom') {
    anchorOrigin = { vertical: 'bottom', horizontal: 'center' };
    transformOrigin = { vertical: 'top', horizontal: 'center' };
    arrowPosition = { top: 'auto', right: 'calc(50% - 10px)', bottom: '-18px', left: 'auto' };
    menuMargin = { mt: '8px' };
    points = '0 10 10 0 20 10';
  }

  return (
    <>
      <Box
        ref={containerRef}
        className={clsx('dropdown-menu', className)}
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          backgroundColor: anchorEl && theme.palette.background.menu,
          opacity: anchorEl ? 1 : opacity,
          transition: [theme.transitions.create(['background-color', 'opacity'])],
          '&:hover': {
            opacity: 1,
          },
          position: 'relative',
        })}
      >
        {icon ? (
          <PowerIconButton
            icon={icon}
            hoverIcon={hoverIcon}
            color={color}
            size={size}
            padding={padding}
            onClick={handleDropdownOpen}
            sx={anchorEl && { opacity: '1 !important' }}
            loading={loading}
            title={title}
          />
        ) : (
          <ButtonBase sx={{ padding: 2, borderRadius: '50%' }} onClick={handleDropdownOpen}>
            <Tooltip title={title}>
              <span>{children}</span>
            </Tooltip>
          </ButtonBase>
        )}
        <SvgIcon
          viewBox="0 0 20 20"
          sx={(theme) => ({
            display: 'block',
            position: 'absolute',
            width: 20,
            height: 20,
            color: 'background.menu',
            zIndex: theme.zIndex.modal + 1,
            opacity: anchorEl ? 1 : 0,
            transition: [theme.transitions.create(['opacity'])],
            pointerEvents: 'none',
            ...arrowPosition,
          })}
        >
          <polygon fill="currentColor" points={points} />
          <polyline fill="none" stroke="rgba(255, 255, 255, 0.3)" points={points} />
        </SvgIcon>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleDropdownClose()}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        slotProps={{
          paper: {
            elevation: 5,
            sx: mergeSxProps({ overflowY: 'auto' }, menuMargin),
          },
        }}
      >
        <MenuItems items={items} selectedItem={selectedItem} onAfterClick={handleDropdownClose} />
      </Menu>
    </>
  );
}
